// Copied from the V3 file on 2026-08-27 for the recommendation boundary; V3 remains unchanged.
(() => {
  'use strict';

  const FAVORITES_CATEGORY_QUERY = '__favorites__';
  const FAVORITES_STORAGE_KEY = 'davinci-coloring-blanket-favorite-design-codes-v1';
  // V4 recently viewed: browser-local selection history (see the RECENTLY VIEWED block).
  const RECENTLY_VIEWED_STORAGE_KEY = 'davinci-coloring-blanket-recently-viewed-v1';

  const CATEGORY_CONFIG = [
    // `query` must match a catalog record category exactly; `label` is display-only.
    // `key` is a stable cbpills-override alias kept when a label is renamed, so external
    // param-generating tooling never breaks (the full lowercased label also still works).
    // `pid` is the NEUTRAL stable override id (p1..p9) — the only key form allowed in the
    // template setting, so themed pill names never enter server HTML (Google signal
    // constraint). pids are permanent: never renumber, append only.
    { label: 'All designs', query: '', countLabel: '1,600+', pid: 'p1' },
    { label: 'Saved', query: FAVORITES_CATEGORY_QUERY, kind: 'favorites', pid: 'p2' },
    { label: 'Flowers', query: 'Flowers', pid: 'p3' },
    { label: 'Animals', query: 'Animals', pid: 'p4' },
    { label: 'Family', query: 'Family', pid: 'p5' },
    { label: 'Unicorns and Fairy Tales', query: 'Unicorns', key: 'unicorns', pid: 'p6' },
    { label: 'Dinosaurs', query: 'Dinosaurs', pid: 'p7' },
    { label: 'Mandalas', query: 'Mandalas', pid: 'p8' },
    { label: 'Scenery', query: 'Scenery', pid: 'p9' },
  ];

  const normalize = (value) => String(value || '').trim().toLowerCase();
  // V3 curation overrides. Two sources, one format:
  //   template setting  — data-pill-overrides / data-shelf-overrides on the results
  //                       section (persistent curation, set in the template JSON or
  //                       theme editor)
  //   URL param         — ?cbpills= / ?cbshelves= (dev preview; wins over the setting,
  //                       persists for the tab via sessionStorage)
  // Precedence: URL param > sessionStorage > template setting > built-in default.
  // ?cbpills=off / ?cbshelves=off shows the stock defaults for this tab, ignoring the
  // template setting too ('off' sentinel in sessionStorage). Values are read at parse
  // time (the script is deferred, so the section DOM exists) and consumed inside
  // renderCategories() / renderShelves(), which run after activation.
  const settingOverride = (attrName) => {
    const host = document.querySelector('[data-coloring-results]');
    return (host && host.getAttribute(attrName)) || '';
  };
  const resolveOverrideRaw = (paramName, attrName) => {
    const param = new URL(window.location.href).searchParams.get(paramName);
    if (param) sessionStorage.setItem(paramName, param);
    const stored = sessionStorage.getItem(paramName) || '';
    if (stored === 'off') return '';
    return stored || settingOverride(attrName);
  };
  const PILL_OVERRIDES = (() => {
    const map = new Map();
    try {
      const raw = resolveOverrideRaw('cbpills', 'data-pill-overrides');
      if (!raw) return map;
      raw.split(',').forEach((pair) => {
        const [key, code] = pair.split(':');
        if (key && code) map.set(normalize(key), normalize(code));
      });
    } catch (_error) { /* ignore */ }
    return map;
  })();
  const SHELF_OVERRIDES = (() => {
    const map = new Map();
    try {
      const raw = resolveOverrideRaw('cbshelves', 'data-shelf-overrides');
      if (!raw) return map;
      raw.split(',').forEach((pair) => {
        const [id, codes] = pair.split(':');
        if (id && codes) map.set(normalize(id), codes.split('|').map(normalize).filter(Boolean));
      });
    } catch (_error) { /* ignore */ }
    return map;
  })();
  // Per-pill design order: codes pinned to the FRONT of the library grid for that pill's
  // view (no search, no shelf filter — those stay natural). Keys are NEUTRAL pids only
  // (p1 = All designs, p3..p9 = category pills; p2/Saved is the shopper's own list).
  // Format: pid:CODE|CODE,pid:CODE|CODE. Same sources and precedence as the others.
  const ORDER_OVERRIDES = (() => {
    const map = new Map();
    try {
      const raw = resolveOverrideRaw('cborder', 'data-order-overrides');
      if (!raw) return map;
      raw.split(',').forEach((pair) => {
        const [pid, codes] = pair.split(':');
        if (pid && codes) map.set(normalize(pid), codes.split('|').map(normalize).filter(Boolean));
      });
    } catch (_error) { /* ignore */ }
    return map;
  })();
  const cleanDesignTitle = (value) =>
    String(value || '')
      .replace(/^(?:Sherpa\s+)?Blanket Coloring Kit with (?:\d+\s+)?Fabric Markers\s*[-–]\s*/i, '')
      .replace(/^Coloring Blanket with (?:\d+\s+)?Fabric Markers\s*[-–]\s*/i, '')
      .trim();
  const normalizeSize = (value) =>
    normalize(value)
      .replace(/[“”″]/g, '"')
      .replace(/\s*[x×]\s*/g, 'x')
      .replace(/\s+/g, '');
  // Media alt-text contract for the per-design thumbnail rail. "design N" in the alt ties
  // the media to Design N; the shared markers tie it to every design; anything else is
  // generic Design-1 imagery (it shows the original artwork).
  const DESIGN_MEDIA_RE = /design\s*(\d+)/i;
  const SHARED_MEDIA_RE = /reverse-side|fabric sample|all designs/i;
  const mediaPath = (value) => {
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (_error) {
      return String(value || '');
    }
  };
  const recordSearchText = (record) =>
    normalize([record.title, record.designCode, record.handle, record.category, record.audience, record.keywords].join(' '));
  const SHELF_CONFIG = [
    {
      id: 'kids',
      title: 'Made for kids',
      matches: (record) =>
        /\b(?:baby|bunny|butterfl(?:y|ies)|cartoon|child|children|cute|dinosaur|fairy|kid|kids|kitten|princess|puppy|tractor|truck|unicorn|vehicle)\b/.test(
          recordSearchText(record)
        ),
    },
    {
      id: 'calm',
      title: 'Calm, detailed designs',
      matches: (record) =>
        (['mandalas', 'scenery'].includes(normalize(record.category)) ||
          /\b(?:botanical|intricate|landscape|mandala|ornate|paisley|zen)\b/.test(recordSearchText(record))) &&
        !['dinosaurs', 'unicorns', 'vehicles'].includes(normalize(record.category)) &&
        !/\b(?:dinosaur|monster truck|unicorn)\b/.test(recordSearchText(record)),
    },
  ];

  const sizedShopifyImage = (value, width) => {
    if (!value || !width) return value;
    try {
      const url = new URL(value, window.location.origin);
      const isShopifyImage = url.hostname === 'cdn.shopify.com' || url.pathname.startsWith('/cdn/shop/');
      if (!isShopifyImage) return value;
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (_error) {
      return value;
    }
  };

  class ColoringBlanketV2Page {
    constructor(root) {
      this.root = root;
      this.forms = [...root.querySelectorAll('[data-coloring-form]')];
      this.addButtons = [...root.querySelectorAll('[data-coloring-add]')];
      this.status = root.querySelector('[data-coloring-status]');
      this.modal = root.querySelector('[data-coloring-modal]');
      this.modalImage = root.querySelector('[data-coloring-modal-image]');
      this.designThumbnail = root.querySelector('[data-coloring-thumbnail]:first-child');
      this.thumbnailRail = root.querySelector('.cbv2-thumbnail-rail');
      this.opener = null;
      this.isAdding = false;
      this.variants = this.readJson('[data-coloring-variants]');
      this.defaultSourceVariants = this.readJson('[data-coloring-default-source-variants]');
      // Full product media list for the per-design thumbnail rail.
      this.mediaLibrary = this.readJson('[data-coloring-media-json]');
      // Legacy products' galleries, fetched on demand, keyed by handle.
      this.legacyMediaCache = new Map();
      // The generic sticky-bar title as rendered by Liquid, restored whenever a numbered
      // design is selected (they carry no public name by design).
      this.stickyTitleDefault =
        root.querySelector('[data-coloring-sticky-title]')?.textContent.trim() || 'Coloring Blanket';
      this.variant = this.findInitialVariant();
      // The variant the page opened on (?variant= deep link = the ad-clicked variant),
      // frozen before any interaction so cart adds can report their origin.
      this.originVariantId = this.variant ? String(this.variant.id) : '';
      this.selectedOptions = this.variant?.options ? [...this.variant.options] : [];
      // Which pool the cart targets. 'variant' = this product's Design-option variants
      // (combined listing). 'legacy' = a catalog design's own product variants
      // (sourceVariantId). The invariant both modes protect: the cart variant's
      // fulfillment linkage prints exactly what the shopper sees.
      this.designMode = 'variant';
      this.currencyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: root.dataset.currency || 'USD',
      });
      this.design = {
        title: root.dataset.defaultDesignName,
        designCode: root.dataset.defaultDesignCode,
        handle: root.dataset.defaultDesignSource,
        id: root.dataset.defaultDesignSourceId,
        image: root.dataset.defaultDesignImage,
        imageAlt: `${root.dataset.defaultDesignName} coloring blanket design`,
        coverImage: root.dataset.defaultDesignCover || root.dataset.defaultDesignImage,
        coverImageAlt: root.dataset.defaultDesignCoverAlt || 'Coloring blanket with fabric markers',
        sourceVariants: this.defaultSourceVariants.length
          ? this.defaultSourceVariants
          : this.genericSizeChoices().map(({ variant }) => ({
              size: variant.options[0] || variant.title,
              price: variant.price,
              compareAtPrice: variant.compare_at_price,
              available: variant.available,
            })),
      };

      root.coloringBlanket = this;
      this.bindOptions();
      this.bindModal();
      this.bindForms();
      this.updateVariant(this.variant, { announce: false });
      // Initialize page imagery/label/cart properties from the initially selected variant
      // so ?variant= deep links land on the right design. reveal:false keeps the
      // "Selected design" labels hidden so the settled no-interaction DOM stays generic;
      // hidden form properties still describe what the variant actually prints.
      this.applyVariantDesign(this.variant, { announce: false, reveal: false });
      this.updateDesignControls();
    }

    readJson(selector) {
      try {
        const value = JSON.parse(this.root.querySelector(selector)?.textContent || '[]');
        return Array.isArray(value) ? value : [];
      } catch (error) {
        console.error('Coloring blanket V2 JSON could not be parsed.', error);
        return [];
      }
    }

    findInitialVariant() {
      const initialId = this.root.querySelector('[data-coloring-variant-id]')?.value;
      return this.variants.find((variant) => String(variant.id) === String(initialId)) || this.variants[0] || null;
    }

    formatMoney(cents) {
      return this.currencyFormatter.format(Number(cents || 0) / 100);
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.setTimeout(() => {
        this.status.textContent = message;
      }, 20);
    }

    bindOptions() {
      this.root.addEventListener('change', (event) => {
        const input = event.target.closest('[data-coloring-option-input]');
        if (!input || !this.root.contains(input) || !input.checked) return;
        const position = Number(input.dataset.optionPosition) - 1;

        // Legacy-mode size click - the cart targets the legacy product's own variant
        // for that size.
        if (input.dataset.coloringLegacySize !== undefined) {
          const sourceVariant = (this.design.sourceVariants || []).find(
            (sv) => normalizeSize(sv.size) === normalizeSize(input.value) && sv.sourceVariantId
          );
          if (!sourceVariant || !sourceVariant.available) {
            input.checked = false;
            this.announce(`${input.value} is sold out for this design.`);
            return;
          }
          this.updateVariant(this.syntheticLegacyVariant(sourceVariant), { announce: true });
          return;
        }

        // Design number clicked while a catalog design was active - leave legacy mode and
        // resolve size by normalized label (legacy size strings need not equal the generic
        // option strings byte-for-byte).
        if (this.designMode === 'legacy' && position === 1) {
          const previousSize = this.variant?.options?.[0] || '';
          const candidates = this.variants.filter((variant) => variant.options[1] === input.value);
          const nextVariant =
            candidates.find(
              (variant) =>
                variant.available && normalizeSize(variant.options[0]) === normalizeSize(previousSize)
            ) ||
            candidates.find((variant) => variant.available) ||
            candidates[0];
          if (!nextVariant) {
            input.checked = false;
            this.announce(`Design ${input.value} is not available right now.`);
            return;
          }
          this.updateVariant(nextVariant, { announce: true });
          this.applyVariantDesign(nextVariant, { announce: false });
          // Selecting a design opens the product modal.
          this.openModal(input);
          return;
        }

        const nextOptions = [...this.selectedOptions];
        nextOptions[position] = input.value;
        const nextVariant = this.variants.find((variant) =>
          variant.options.every((value, index) => value === nextOptions[index])
        );

        if (!nextVariant || input.dataset.sourceAvailable === 'false') {
          input.checked = false;
          this.syncOptionInputs();
          this.announce(`${input.value} is not available with the current selection.`);
          return;
        }

        this.selectedOptions = [...nextVariant.options];
        this.updateVariant(nextVariant, { announce: true });
        // A Design option click is a design selection - sync the page, and a Design click
        // (not a size click) opens the product modal.
        this.applyVariantDesign(nextVariant, { announce: false });
        if (position === 1) this.openModal(input);
      });
    }

    genericVariantForSize(size) {
      const matches = this.variants.filter(
        (variant) => normalizeSize(variant.options?.[0] || variant.title) === normalizeSize(size)
      );
      if (!matches.length) return null;
      return (
        matches.find(
          (variant) =>
            variant.available &&
            variant.options.every(
              (value, index) => index === 0 || !this.selectedOptions[index] || value === this.selectedOptions[index]
            )
        ) ||
        matches.find((variant) => variant.available) ||
        matches[0]
      );
    }

    genericSizeChoices() {
      const sizes = new Set();
      return this.variants.reduce((choices, variant) => {
        const size = variant.options?.[0] || variant.title;
        const key = normalizeSize(size);
        if (sizes.has(key)) return choices;
        sizes.add(key);
        choices.push({ size, sourceAvailable: true, variant: this.genericVariantForSize(size) || variant });
        return choices;
      }, []);
    }

    designSizeChoices() {
      // Legacy mode sells the legacy product's OWN variants (sourceVariantId), never a
      // generic variant wearing legacy properties. A legacy record without usable
      // sourceVariantIds yields no choices, which drives the existing unavailable branch
      // instead of silently mis-selling the generic design.
      if (this.designMode === 'legacy') {
        const sourceVariants = Array.isArray(this.design.sourceVariants)
          ? this.design.sourceVariants.filter((sv) => sv?.size && sv?.sourceVariantId)
          : [];
        const sizes = new Set();
        return sourceVariants.reduce((choices, sourceVariant) => {
          const key = normalizeSize(sourceVariant.size);
          if (sizes.has(key)) return choices;
          sizes.add(key);
          choices.push({
            legacy: true,
            size: sourceVariant.size,
            available: Boolean(sourceVariant.available),
            sourceVariant,
          });
          return choices;
        }, []);
      }
      return this.genericSizeChoices().map((choice) => ({
        legacy: false,
        size: choice.size,
        available: Boolean(choice.variant?.available),
        variant: choice.variant,
      }));
    }

    // A catalog design's size choice as a cart-ready pseudo-variant.
    syntheticLegacyVariant(sourceVariant) {
      return {
        id: sourceVariant.sourceVariantId,
        title: sourceVariant.size,
        options: [sourceVariant.size],
        price: sourceVariant.price,
        compare_at_price: sourceVariant.compareAtPrice,
        available: Boolean(sourceVariant.available),
      };
    }

    renderDesignSizeControls() {
      // Mode-aware. Legacy choices carry data-coloring-legacy-size + their
      // sourceVariantId; generic choices keep the original behavior.
      const choices = this.designSizeChoices();
      this.root.querySelectorAll('[data-coloring-options-surface]').forEach((surface) => {
        const values = surface.querySelector('[data-coloring-option-group][data-option-position="1"] .cbv2-option-values');
        if (!values) return;
        const fragment = document.createDocumentFragment();
        const surfaceName = surface.dataset.coloringOptionsSurface || 'product';

        choices.forEach((choice, index) => {
          const input = document.createElement('input');
          const label = document.createElement('label');
          const enabled = choice.available;
          const id = `Cbv2DynamicSize-${this.root.dataset.sectionId}-${surfaceName}-${index}`;

          input.className = 'cbv2-option-input';
          input.type = 'radio';
          input.id = id;
          input.name = `cbv2-${surfaceName}-option-${this.root.dataset.sectionId}-1`;
          input.dataset.coloringOptionInput = '';
          input.dataset.optionPosition = '1';
          input.dataset.sourceAvailable = String(enabled);
          input.disabled = !enabled;

          if (choice.legacy) {
            input.value = choice.size;
            input.dataset.coloringLegacySize = '';
            input.dataset.sourceVariantId = String(choice.sourceVariant.sourceVariantId);
            input.checked = normalizeSize(choice.size) === normalizeSize(this.variant?.options?.[0]);
          } else {
            input.value = choice.variant?.options?.[0] || choice.size;
            input.checked = Boolean(choice.variant && String(choice.variant.id) === String(this.variant?.id));
          }

          label.className = 'cbv2-option-label';
          label.htmlFor = id;
          label.textContent = choice.size;
          if (!enabled) {
            label.title = `${choice.size} is sold out for this design.`;
          }
          fragment.append(input, label);
        });
        values.replaceChildren(fragment);
      });
    }

    updateVariant(variant, { announce = false } = {}) {
      if (!variant) return;
      this.variant = variant;
      this.selectedOptions = [...variant.options];

      this.root.querySelectorAll('[data-coloring-variant-id]').forEach((input) => {
        input.value = variant.id;
      });
      this.root.querySelectorAll('[data-coloring-price]').forEach((node) => {
        node.textContent = this.formatMoney(variant.price);
      });
      this.root.querySelectorAll('[data-coloring-compare-price]').forEach((node) => {
        const showCompare = Number(variant.compare_at_price || 0) > Number(variant.price || 0);
        node.hidden = !showCompare;
        node.textContent = showCompare ? this.formatMoney(variant.compare_at_price) : '';
      });
      this.root.querySelectorAll('[data-coloring-selected-size]').forEach((node) => {
        node.textContent = variant.options[0] || variant.title;
      });
      this.root.querySelectorAll('[data-coloring-availability]').forEach((node) => {
        node.textContent = variant.available ? 'Available' : 'Sold out';
        node.classList.toggle('is-unavailable', !variant.available);
      });

      this.syncOptionInputs();
      this.updateOptionAvailability();
      this.updateAddButtons();

      if (announce) {
        this.announce(
          `${variant.options[0] || variant.title} selected. ${this.formatMoney(variant.price)}. ${
            variant.available ? 'Available.' : 'Sold out.'
          }`
        );
      }
    }

    syncOptionInputs() {
      this.root.querySelectorAll('[data-coloring-option-input]').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        input.checked = this.selectedOptions[position] === input.value;
      });
    }

    updateOptionAvailability() {
      // Legacy mode manages its own size availability at render time (sourceVariants),
      // and every Design number stays clickable as the way back to the generic pool -
      // the generic-variant math below would wrongly disable both.
      if (this.designMode === 'legacy') {
        this.root.querySelectorAll('[data-coloring-option-input]').forEach((input) => {
          if (input.dataset.coloringLegacySize !== undefined) return;
          input.disabled = false;
        });
        return;
      }
      this.root.querySelectorAll('[data-coloring-option-input]').forEach((input) => {
        const position = Number(input.dataset.optionPosition) - 1;
        const isPossible = this.variants.some((variant) => {
          if (!variant.available || variant.options[position] !== input.value) return false;
          return variant.options.every((value, index) => index === position || value === this.selectedOptions[index]);
        });
        input.disabled = input.dataset.sourceAvailable === 'false' || !isPossible;
      });
    }

    updateAddButtons() {
      this.addButtons.forEach((button) => {
        button.disabled = this.isAdding || !this.variant?.available;
        const label = button.querySelector('[data-coloring-add-label]');
        if (label) label.textContent = this.isAdding ? 'Adding…' : this.variant?.available ? 'Add to cart' : 'Sold out';
        button.classList.toggle('is-loading', this.isAdding);
      });
    }

    selectDesign(record, { announce = true, updateUrl = true } = {}) {
      if (!record?.title || !record?.designCode || !record?.handle || !record?.image) return;
      const previousSize = this.variant?.options?.[0] || '';
      const designTitle = cleanDesignTitle(record.title) || record.title;
      // A catalog design switches the cart to the legacy product's own variants
      // (operator decision: route the grid to the legacy per-design products).
      this.designMode = 'legacy';
      this.design = {
        title: designTitle,
        designCode: String(record.designCode).toUpperCase(),
        handle: record.handle,
        id: String(record.id || ''),
        image: record.image,
        imageAlt: record.imageAlt || `${record.title} coloring blanket design`,
        coverImage: record.coverImage || record.image,
        coverImageAlt: record.coverImageAlt || `${record.title} blanket cover`,
        sourceVariants: Array.isArray(record.sourceVariants) ? record.sourceVariants : [],
      };

      this.root.querySelectorAll('[data-coloring-design-name-text]').forEach((node) => {
        node.textContent = this.design.title;
      });
      this.root.querySelectorAll('[data-coloring-selected-design-label]').forEach((node) => {
        node.hidden = false;
      });
      this.root.querySelectorAll('[data-coloring-selected-design-summary]').forEach((node) => {
        node.hidden = false;
      });
      this.root.querySelectorAll('[data-coloring-selected-design-summary-text]').forEach((node) => {
        node.textContent = this.design.title;
      });
      this.root.querySelectorAll('[data-coloring-sticky-title]').forEach((node) => {
        node.textContent = this.design.title;
      });
      this.root.querySelectorAll('[data-coloring-design-name]').forEach((input) => {
        input.value = this.design.title;
      });
      this.root.querySelectorAll('[data-coloring-design-id]').forEach((input) => {
        input.value = this.design.designCode;
      });
      this.root.querySelectorAll('[data-coloring-design-source]').forEach((input) => {
        input.value = this.design.handle;
      });
      this.root.querySelectorAll('[data-coloring-design-image]').forEach((input) => {
        input.value = this.design.image;
      });

      this.applyDesignImages(this.design.coverImage, this.design.coverImageAlt);

      const choices = this.designSizeChoices();
      const preservedChoice = choices.find(
        (choice) => choice.available && normalizeSize(choice.size) === normalizeSize(previousSize)
      );
      const selectedChoice = preservedChoice || choices.find((choice) => choice.available) || null;
      if (selectedChoice?.sourceVariant) {
        this.updateVariant(this.syntheticLegacyVariant(selectedChoice.sourceVariant), { announce: false });
      } else {
        this.variant = null;
        this.selectedOptions = [];
        this.root.querySelectorAll('[data-coloring-variant-id]').forEach((input) => {
          input.value = '';
        });
        this.root.querySelectorAll('[data-coloring-selected-size]').forEach((node) => {
          node.textContent = choices[0]?.size || 'Unavailable';
        });
        this.root.querySelectorAll('[data-coloring-availability]').forEach((node) => {
          node.textContent = 'Unavailable';
          node.classList.add('is-unavailable');
        });
        this.updateAddButtons();
      }

      this.renderDesignSizeControls();
      this.syncOptionInputs();
      this.updateOptionAvailability();
      // Rebuild the modal rail for this design, then upgrade it with the legacy
      // product's real gallery (async).
      this.renderThumbnailRail();
      this.loadLegacyRailMedia(this.design);
      this.updateDesignControls();

      if (updateUrl) {
        const url = new URL(window.location.href);
        const designState = new URLSearchParams();
        designState.set('design', this.design.designCode);
        url.searchParams.delete('design');
        url.hash = designState.toString();
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      }

      this.root.dispatchEvent(
        new CustomEvent('coloring:design-change', { bubbles: true, detail: { design: this.design } })
      );
      if (announce) {
        this.announce(
          selectedChoice?.sourceVariant
            ? `${this.design.title} selected. ${selectedChoice.size} selected.`
            : `${this.design.title} selected. No compatible blanket size is currently available.`
        );
      }
    }

    // The Design option is variant-backed (combined listing). Picking a design value makes
    // that variant's cover the page imagery, relabels "Selected design" as the neutral
    // "Design N", and rewrites the legacy cart properties so order line items describe
    // what the variant actually prints. Values without variant media fall back to the
    // default cover. The catalog grid flow (selectDesign) deliberately does NOT route
    // through here. reveal:false (constructor only) syncs everything except unhiding the
    // "Selected design" labels, keeping the settled no-interaction DOM generic.
    applyVariantDesign(variant, { announce = false, reveal = true } = {}) {
      const designValue = variant?.options?.[1];
      if (!designValue) return;
      const wasLegacy = this.designMode === 'legacy';
      this.designMode = 'variant';
      const label = `Design ${designValue}`;
      const image = variant.featured_image?.src || this.root.dataset.defaultDesignCover;
      const alt = variant.featured_image?.alt || `${label} coloring blanket cover`;

      this.design = {
        title: label,
        designCode: '',
        handle: '',
        id: '',
        image,
        imageAlt: alt,
        coverImage: image,
        coverImageAlt: alt,
        sourceVariants: [],
      };

      this.root.querySelectorAll('[data-coloring-design-name-text]').forEach((node) => {
        node.textContent = label;
      });
      if (reveal) {
        this.root.querySelectorAll('[data-coloring-selected-design-label]').forEach((node) => {
          node.hidden = false;
        });
        this.root.querySelectorAll('[data-coloring-selected-design-summary]').forEach((node) => {
          node.hidden = false;
        });
      }
      this.root.querySelectorAll('[data-coloring-selected-design-summary-text]').forEach((node) => {
        node.textContent = label;
      });
      // Reset the sticky-bar title to the generic product name. A legacy selection writes
      // its own name there; numbered designs must not leave it stuck nor show a
      // design-specific reference (operator preference, Google-signal constraint).
      this.root.querySelectorAll('[data-coloring-sticky-title]').forEach((node) => {
        node.textContent = this.stickyTitleDefault;
      });
      this.root.querySelectorAll('[data-coloring-design-name]').forEach((input) => {
        input.value = label;
      });
      this.root.querySelectorAll('[data-coloring-design-id]').forEach((input) => {
        input.value = variant.sku || '';
      });
      this.root.querySelectorAll('[data-coloring-design-source]').forEach((input) => {
        input.value = 'coloring-blanket';
      });
      this.root.querySelectorAll('[data-coloring-design-image]').forEach((input) => {
        input.value = image || '';
      });

      this.applyDesignImages(image, alt);
      // Returning from legacy mode must restore the generic size controls (three sizes,
      // generic variant targeting) that the catalog flow replaced.
      if (wasLegacy) {
        this.renderDesignSizeControls();
      }
      this.syncOptionInputs();
      this.updateOptionAvailability();
      // Per-design secondary images in the modal rail.
      this.renderThumbnailRail();
      this.updateDesignControls();
      if (announce) this.announce(`${label} selected.`);
    }

    // One-tap return to the generic product from anywhere (Gus, 2026-08-14): after a
    // legacy pick there was no UI path back to the variant-backed product a shopper's
    // ad click landed on. Restores variant mode on a real product variant (preserving
    // the chosen size when available) and, by default, opens the modal on it.
    resetToGeneric({ openModal = true } = {}) {
      const currentSize = this.variant?.options?.[0] || '';
      const target =
        this.variants.find((variant) => variant.available && normalizeSize(variant.options[0]) === normalizeSize(currentSize)) ||
        this.variants.find((variant) => variant.available) ||
        this.variants[0];
      if (!target) return;
      this.updateVariant(target, { announce: false });
      this.applyVariantDesign(target, { announce: true, reveal: true });
      if (openModal) this.openModal();
    }

    // Shared image writer for both design flows (variant-backed Design option and the
    // legacy catalog grid).
    applyDesignImages(imageUrl, alt) {
      if (!imageUrl) return;
      const heroImage = this.root.querySelector('.cbv2-design-image');
      if (heroImage) {
        heroImage.src = sizedShopifyImage(imageUrl, 900);
        heroImage.srcset = [360, 540, 720, 900]
          .map((width) => `${sizedShopifyImage(imageUrl, width)} ${width}w`)
          .join(', ');
        heroImage.alt = alt || '';
      }
      if (this.modalImage) {
        this.modalImage.src = sizedShopifyImage(imageUrl, 900);
        // ADDED 2026-08-13: keep the modal image responsive on swaps (mirrors the hero).
        this.modalImage.srcset = [360, 540, 720, 900]
          .map((width) => `${sizedShopifyImage(imageUrl, width)} ${width}w`)
          .join(', ');
        this.modalImage.alt = alt || '';
      }
      const stickyImage = this.root.querySelector('[data-coloring-sticky-image]');
      if (stickyImage) stickyImage.src = sizedShopifyImage(imageUrl, 160);
      const thumbnailImage = this.root.querySelector('[data-coloring-thumbnail-design-image]');
      if (thumbnailImage) thumbnailImage.src = sizedShopifyImage(imageUrl, 160);
      if (this.designThumbnail) {
        this.designThumbnail.dataset.mediaSrc = sizedShopifyImage(imageUrl, 900);
        this.designThumbnail.dataset.mediaAlt = alt || '';
      }
    }

    // Secondary media for the current design, per the alt-text contract (shared markers
    // on every design; "design N" media on that design; untagged generic media on Design 1
    // only; legacy catalog designs get shared media only). The current cover is excluded
    // so the first (design) thumbnail is never duplicated.
    railMediaForDesign() {
      const coverPath = mediaPath(this.design?.coverImage || '');
      const designValue = this.designMode === 'variant' ? String(this.variant?.options?.[1] || '') : '';
      return this.mediaLibrary
        .filter((item) => {
          if (!item?.src || mediaPath(item.src) === coverPath) return false;
          const alt = String(item.alt || '');
          if (SHARED_MEDIA_RE.test(alt)) return true;
          const designMatch = alt.match(DESIGN_MEDIA_RE);
          if (designMatch) return designValue === designMatch[1];
          return designValue === '1';
        })
        .slice(0, 4);
    }

    // Rebuild the modal thumbnail rail for the current design. The first (design cover)
    // thumbnail is kept and re-selected; the rest are replaced. An explicit media list
    // overrides the alt-contract filter (used for fetched legacy galleries).
    renderThumbnailRail(mediaOverride) {
      if (!this.thumbnailRail || !this.designThumbnail) return;
      this.thumbnailRail.querySelectorAll('[data-coloring-thumbnail]').forEach((button) => {
        if (button !== this.designThumbnail) button.remove();
      });
      (mediaOverride || this.railMediaForDesign()).forEach((item, index) => {
        const button = document.createElement('button');
        button.className = 'cbv2-thumbnail';
        button.type = 'button';
        button.dataset.coloringThumbnail = '';
        button.dataset.mediaSrc = sizedShopifyImage(item.src, 1000);
        button.dataset.mediaAlt = item.alt || '';
        button.setAttribute('aria-label', `View product photo ${index + 1}`);
        button.setAttribute('aria-pressed', 'false');
        const image = document.createElement('img');
        image.src = sizedShopifyImage(item.src, 160);
        image.alt = '';
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 96;
        image.height = 96;
        button.append(image);
        this.thumbnailRail.append(button);
      });
      this.selectThumbnail(this.designThumbnail);
    }

    // Legacy designs' secondary images live on the legacy product itself (the catalog
    // JSON carries only the cover), so fetch its public product JSON on demand and
    // rebuild the rail from the real gallery. Cached per handle; any failure keeps the
    // shared-media fallback rail already rendered by selectDesign.
    async loadLegacyRailMedia(design) {
      if (this.designMode !== 'legacy' || !design?.handle) return;
      const requestedCode = design.designCode;
      try {
        let images = this.legacyMediaCache.get(design.handle);
        if (!images) {
          const response = await fetch(`/products/${design.handle}.js`, {
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) return;
          const product = await response.json();
          images = Array.isArray(product.images) ? product.images : [];
          this.legacyMediaCache.set(design.handle, images);
        }
        // The shopper may have moved on while the request was in flight.
        if (this.designMode !== 'legacy' || this.design.designCode !== requestedCode) return;
        const coverPath = mediaPath(this.design.coverImage || '');
        const secondary = images
          .filter((src) => src && mediaPath(src) !== coverPath)
          .slice(0, 4)
          .map((src, index) => ({ src, alt: `${this.design.title} product photo ${index + 1}` }));
        if (secondary.length) this.renderThumbnailRail(secondary);
      } catch (_error) {
        /* keep the fallback rail */
      }
    }

    updateDesignControls() {
      document.querySelectorAll('[data-coloring-design-card]').forEach((card) => {
        const isSelected = normalize(card.dataset.designCode) === normalize(this.design.designCode);
        card.classList.toggle('is-selected', isSelected);
        card.setAttribute('aria-pressed', String(isSelected));
      });
      // A legacy catalog design is not one of this product's Design variants, so the
      // "Choose a design" variant swatch picker would only switch the shopper away from
      // their selection — hide it (hero row and modal) while a legacy design is active;
      // it returns as soon as a variant-backed Design or the generic default is shown.
      const legacyActive = this.designMode === 'legacy';
      this.root.querySelectorAll('[data-coloring-option-group][data-option-position="2"]').forEach((group) => {
        group.hidden = legacyActive;
      });
      this.root.querySelectorAll('.cbv2-hero-designs').forEach((wrap) => {
        wrap.hidden = legacyActive;
      });
      // Square legacy covers get a taller modal image box so they fill more of the
      // white space (generic landscape photos already fill the width).
      this.modalImage?.closest('.cbv2-modal-main')?.classList.toggle('cbv2-modal-main--tall', legacyActive);
      // The modal's return-to-classic link only makes sense while a legacy design is
      // shown — resetToGeneric() is its click target (bound in bindModal).
      this.root.querySelectorAll('[data-coloring-classic-return]').forEach((node) => {
        node.hidden = !legacyActive;
      });
    }

    openModal(opener) {
      if (!this.modal || this.modal.open) return;
      this.opener = opener || this.root.querySelector('[data-coloring-open-modal]');
      this.modal.showModal();
      document.body.classList.add('cbv2-modal-open');
      this.modal.querySelector('[data-coloring-close-modal]')?.focus();
    }

    bindModal() {
      if (!this.modal) return;
      this.root.querySelectorAll('[data-coloring-open-modal]').forEach((button) => {
        button.addEventListener('click', () => this.openModal(button));
      });
      this.modal.querySelector('[data-coloring-close-modal]')?.addEventListener('click', () => this.modal.close());
      // Swap the open modal back to the generic product in place (no close/reopen).
      this.modal.querySelector('[data-coloring-classic-return]')?.addEventListener('click', () => this.resetToGeneric({ openModal: false }));
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) this.modal.close();
      });
      this.modal.addEventListener('close', () => {
        document.body.classList.remove('cbv2-modal-open');
        this.opener?.focus();
      });
      // Delegated so JS-rebuilt rail thumbnails stay clickable.
      this.thumbnailRail?.addEventListener('click', (event) => {
        const thumbnail = event.target.closest('[data-coloring-thumbnail]');
        if (thumbnail) this.selectThumbnail(thumbnail);
      });
    }

    selectThumbnail(thumbnail) {
      if (!thumbnail || !this.modalImage) return;
      this.root.querySelectorAll('[data-coloring-thumbnail]').forEach((button) => {
        const isSelected = button === thumbnail;
        button.classList.toggle('is-selected', isSelected);
        button.setAttribute('aria-pressed', String(isSelected));
      });
      this.modalImage.src = thumbnail.dataset.mediaSrc;
      // ADDED 2026-08-13: thumbnail-driven swaps stay responsive too.
      this.modalImage.srcset = [360, 540, 720, 900]
        .map((width) => `${sizedShopifyImage(thumbnail.dataset.mediaSrc, width)} ${width}w`)
        .join(', ');
      this.modalImage.alt = thumbnail.dataset.mediaAlt || '';
    }

    bindForms() {
      this.forms.forEach((form) => {
        form.addEventListener('submit', async (event) => {
          if (!this.variant?.available || this.isAdding) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          await this.addToCart(form);
        });
      });
    }

    async addToCart(form) {
      this.isAdding = true;
      this.updateAddButtons();
      try {
        const body = new FormData(form);
        // Origin linkage: a legacy-mode add carts a DIFFERENT product than the page the
        // shopper landed on, which orphans the order from the ad click. Record the
        // landing context on every line item. Underscore properties stay hidden at
        // checkout; values are neutral (no themed design words).
        body.set('properties[_Origin Page]', window.location.pathname);
        if (this.originVariantId) body.set('properties[_Origin Variant]', this.originVariantId);
        body.set('properties[_Selection Mode]', this.designMode);
        const response = await fetch(this.root.dataset.cartAddUrl, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body,
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'The item could not be added.');

        const cartResponse = await fetch(`${this.root.dataset.cartUrl}.js`, {
          headers: { Accept: 'application/json' },
        });
        const cart = cartResponse.ok ? await cartResponse.json() : null;
        if (cart) {
          this.root.querySelectorAll('[data-coloring-cart-count]').forEach((node) => {
            node.textContent = cart.item_count;
          });
        }
        this.announce(`${this.design.title} coloring blanket added to cart.`);
      } catch (error) {
        console.error('Coloring blanket V2 add-to-cart failed.', error);
        this.announce(error.message || 'The item could not be added to cart.');
      } finally {
        this.isAdding = false;
        this.updateAddButtons();
      }
    }
  }

  class ColoringBlanketV2Results {
    constructor(root, page) {
      this.root = root;
      this.page = page;
      this.grid = root.querySelector('[data-coloring-design-grid]');
      this.categories = root.querySelector('[data-coloring-categories]');
      this.categoryDots = root.querySelector('[data-coloring-category-dots]');
      this.shelves = root.querySelector('[data-coloring-shelves]');
      this.prompt = root.querySelector('[data-coloring-browser-prompt]');
      this.search = root.querySelector('[data-coloring-search]');
      this.clear = root.querySelector('[data-coloring-search-clear]');
      this.empty = root.querySelector('[data-coloring-empty]');
      this.emptyTitle = root.querySelector('[data-coloring-empty-title]');
      this.emptyCopy = root.querySelector('[data-coloring-empty-copy]');
      this.pagination = root.querySelector('[data-coloring-pagination]');
      this.previous = root.querySelector('[data-coloring-page-previous]');
      this.next = root.querySelector('[data-coloring-page-next]');
      this.pageStatus = root.querySelector('[data-coloring-page-status]');
      this.resultCount = root.querySelector('[data-coloring-results-count]');
      this.resultTitle = root.querySelector('[data-coloring-results-title]');
      this.resultsHeading = root.querySelector('[data-coloring-results-heading]');
      this.total = root.querySelector('[data-coloring-catalog-total]');
      this.savedCount = root.querySelector('[data-coloring-saved-count]');
      this.pageSize = Number(root.dataset.pageSize) || 12;
      this.records = [];
      this.filtered = [];
      this.currentPage = 0;
      this.category = '';
      this.activeShelf = '';
      this.searchTimer = null;
      this.categoryConfig = CATEGORY_CONFIG;
      this.shelfConfig = SHELF_CONFIG;
      // V3 (2026-08-14, Gus's spec): curated/rule shelves show up to 100 (horizontal
      // rail, images lazy-load on approach); the shopper's Saved shelf caps at 25.
      this.shelfSize = 100;
      this.savedShelfSize = 25;
      this.favoriteCodes = this.readFavoriteCodes();
      this.activated = false;
      this.loaded = false;
      this.loadPromise = null;
      this.catalog = null;
      this.catalogPromise = null;
      this.fragmentDesignCode = this.readFragmentDesignCode();
      this.legacyDesignCode = new URL(window.location.href).searchParams.get('design') || '';
      this.boundScrollActivation = () => this.activateFromScroll();
      this.boundPointerActivation = (event) => this.activateFromPointer(event);
      this.boundKeyActivation = (event) => this.activateFromKey(event);
      this.bind();
      this.updateInitialSavedCount();
      // V4 recommendations: wire the mount before the catalog prefetch and any activation
      // (the feature block sits at the end of this class).
      this.initRecommendations();
      // V4 recently viewed: wire the history mount and render any stored entries.
      this.initRecentlyViewed();
      this.scheduleCatalogPrefetch();
      if (this.fragmentDesignCode) this.activate({ announceSelection: false });
    }

    readFragmentDesignCode() {
      const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
      return fragment.get('design') || '';
    }

    readFavoriteCodes() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(FAVORITES_STORAGE_KEY) || '[]');
        if (!Array.isArray(stored)) return new Set();
        return new Set(stored.map((code) => normalize(code)).filter(Boolean));
      } catch (_error) {
        return new Set();
      }
    }

    persistFavoriteCodes() {
      try {
        window.localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify([...this.favoriteCodes]));
      } catch (_error) {
        this.page.announce('This browser could not save favorites between visits.');
      }
    }

    updateInitialSavedCount() {
      if (!this.savedCount) return;
      const count = this.favoriteCodes.size;
      this.savedCount.textContent = `${count.toLocaleString()} saved in this browser`;
    }

    isFavorite(record) {
      return this.favoriteCodes.has(normalize(record?.designCode));
    }

    toggleFavorite(record, sourceButton) {
      const code = normalize(record?.designCode);
      if (!code) return;
      const wasFavorite = this.favoriteCodes.has(code);
      if (wasFavorite) this.favoriteCodes.delete(code);
      else this.favoriteCodes.add(code);
      this.persistFavoriteCodes();
      this.updateInitialSavedCount();

      const sourceScope = sourceButton?.dataset.favoriteScope || '';
      if (this.category === FAVORITES_CATEGORY_QUERY) {
        this.applyFilters();
      } else if (!this.search?.value.trim() && !this.category && !this.activeShelf) {
        this.renderShelves();
        this.updateFavoriteButtons();
      } else {
        this.updateFavoriteButtons();
      }
      this.updateFavoriteCategory();

      if (!sourceButton?.isConnected) {
        const replacement = [...this.root.querySelectorAll('[data-coloring-favorite]')].find(
          (button) => normalize(button.dataset.designCode) === code && button.dataset.favoriteScope === sourceScope
        );
        (replacement || this.search)?.focus();
      }
      this.page.announce(`${cleanDesignTitle(record.title) || record.title} ${wasFavorite ? 'removed from' : 'saved to'} favorites.`);
    }

    scheduleCatalogPrefetch() {
      const startPrefetch = () => {
        this.prefetchCatalog().catch(() => {});
      };
      const scheduleAfterLoad = () => {
        if ('requestIdleCallback' in window) window.requestIdleCallback(startPrefetch);
        else window.setTimeout(startPrefetch, 0);
      };
      if (document.readyState === 'complete') scheduleAfterLoad();
      else window.addEventListener('load', scheduleAfterLoad, { once: true });
    }

    prefetchCatalog() {
      if (this.catalogPromise) return this.catalogPromise;
      this.catalogPromise = (async () => {
        const response = await fetch(this.root.dataset.catalogUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`Catalog request returned ${response.status}.`);
        const catalog = await response.json();
        if (catalog.schemaVersion !== 2 || !Array.isArray(catalog.products)) {
          throw new Error('Catalog schema is not supported.');
        }
        this.catalog = catalog;
        return catalog;
      })().catch((error) => {
        this.catalogPromise = null;
        throw error;
      });
      // V4 recommendations: the first recommendation request waits for the catalog to
      // settle either way (success feeds the fallback; failure still unblocks the API path).
      this.catalogPromise.then(() => this.markCatalogSettled(), () => this.markCatalogSettled());
      return this.catalogPromise;
    }

    bind() {
      document.addEventListener('pointerdown', this.boundPointerActivation, { passive: true });
      document.addEventListener('keydown', this.boundKeyActivation);
      this.root.addEventListener('focusin', (event) => {
        if (!event.target.closest('[data-coloring-hybrid-action]')) this.activate();
      });
      window.addEventListener('scroll', this.boundScrollActivation, { passive: true });

      this.search?.addEventListener('input', () => {
        this.activate();
        this.activeShelf = '';
        this.selectCategory('');
        this.clear.hidden = !this.search.value;
        window.clearTimeout(this.searchTimer);
        this.searchTimer = window.setTimeout(() => this.applyFilters(), 120);
      });
      // The classic-product card above the browse heading: one tap back to the generic
      // product with its Design variants, modal opened on it.
      this.root.querySelector('[data-coloring-classic-card]')?.addEventListener('click', () => {
        this.page.resetToGeneric();
      });
      this.clear?.addEventListener('click', () => {
        this.activate();
        this.search.value = '';
        this.clear.hidden = true;
        this.search.focus();
        this.applyFilters();
      });
      this.root.addEventListener('click', (event) => {
        const hybridButton = event.target.closest('[data-coloring-hybrid-action]');
        if (hybridButton && this.root.contains(hybridButton)) {
          const action = hybridButton.dataset.coloringHybridAction;
          const loading = this.activate();
          if (action === 'details') {
            this.page.openModal(this.page.root.querySelector('[data-coloring-open-modal]'));
          } else if (action === 'saved') {
            loading?.then(() => this.showShelf('saved'));
          } else {
            loading?.then(() => this.categories?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
          }
          return;
        }

        const categoryButton = event.target.closest('[data-coloring-category]');
        if (categoryButton && this.root.contains(categoryButton)) {
          this.activeShelf = '';
          this.selectCategory(categoryButton.dataset.coloringCategory || '');
          this.applyFilters();
          return;
        }

        const shelfButton = event.target.closest('[data-coloring-shelf-see-all]');
        if (shelfButton && this.root.contains(shelfButton)) this.showShelf(shelfButton.dataset.coloringShelfSeeAll);
      });
      this.previous?.addEventListener('click', () => this.goToPage(this.currentPage - 1));
      this.next?.addEventListener('click', () => this.goToPage(this.currentPage + 1));
      document.addEventListener('coloring:design-change', () => this.updateSelectedCards());
    }

    activateFromPointer(event) {
      if (this.activated) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[data-coloring-page], [data-coloring-results]')) return;
      if (target.closest('[data-coloring-hybrid-action]')) return;
      this.activate();
    }

    activateFromKey(event) {
      if (this.activated || event.key === 'Tab' || event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target instanceof Element ? event.target : null;
      if (!target?.closest('[data-coloring-page], [data-coloring-results]')) return;
      if (target.closest('[data-coloring-hybrid-action]')) return;
      this.activate();
    }

    selectCategory(query) {
      this.category = query;
      this.root.querySelectorAll('[data-coloring-category]').forEach((candidate) => {
        const selected = normalize(candidate.dataset.coloringCategory) === normalize(query);
        candidate.classList.toggle('is-selected', selected);
        candidate.setAttribute('aria-pressed', String(selected));
      });
    }

    showShelf(shelfId) {
      if (!this.loaded || (shelfId !== 'saved' && !this.shelfConfig.some((shelf) => shelf.id === shelfId))) return;
      if (this.search) this.search.value = '';
      if (this.clear) this.clear.hidden = true;
      if (shelfId === 'saved') {
        this.activeShelf = '';
        this.selectCategory(FAVORITES_CATEGORY_QUERY);
      } else {
        this.activeShelf = shelfId;
        this.selectCategory('');
      }
      this.applyFilters();
      this.root.querySelector('.cbv2-results-heading')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    activateFromScroll() {
      if (this.activated || window.scrollY <= 0) return;
      const bounds = this.root.getBoundingClientRect();
      if (bounds.top <= window.innerHeight + Math.min(240, window.innerHeight * 0.25)) this.activate();
    }

    activate({ announceSelection = true } = {}) {
      if (this.activated) return this.loadPromise;
      this.activated = true;
      this.root.dataset.coloringActivated = 'true';
      window.removeEventListener('scroll', this.boundScrollActivation);
      document.removeEventListener('pointerdown', this.boundPointerActivation);
      document.removeEventListener('keydown', this.boundKeyActivation);
      this.renderCategories();
      if (this.prompt) this.prompt.hidden = true;
      if (this.resultsHeading) this.resultsHeading.hidden = false;
      if (this.grid) {
        this.grid.hidden = false;
        this.grid.setAttribute('aria-busy', 'true');
      }
      if (this.resultCount) this.resultCount.textContent = 'Loading designs…';
      this.loadPromise = this.load({ announceSelection });
      return this.loadPromise;
    }

    renderCategories() {
      if (!this.categories) return;
      const fragment = document.createDocumentFragment();
      this.categoryConfig.forEach((item, index) => {
        const button = document.createElement('button');
        button.className = `cbv2-category-card${index === 0 ? ' is-selected' : ''}`;
        button.type = 'button';
        button.dataset.coloringCategory = item.query;
        if (item.kind) button.dataset.categoryKind = item.kind;
        button.setAttribute('aria-pressed', String(index === 0));

        const art = document.createElement('span');
        art.className = 'cbv2-category-art';
        art.setAttribute('aria-hidden', 'true');
        const overrideCode = PILL_OVERRIDES.get(item.pid) || PILL_OVERRIDES.get(normalize(item.label)) || (item.key ? PILL_OVERRIDES.get(normalize(item.key)) : undefined);
        const representative = item.kind === 'favorites'
          ? null
          : (overrideCode && this.records.find((r) => normalize(r.designCode) === overrideCode))
            || this.records.find((record) => !item.query || normalize(record.category) === normalize(item.query));
        if (item.kind === 'favorites') {
          const icon = document.createElement('i');
          icon.className = `bi ${this.favoriteCodes.size ? 'bi-heart-fill' : 'bi-heart'}`;
          art.append(icon);
        } else if (representative?.image) {
          const image = document.createElement('img');
          image.src = sizedShopifyImage(representative.image, 320);
          image.alt = '';
          image.loading = 'lazy';
          image.decoding = 'async';
          image.width = 320;
          image.height = 320;
          art.append(image);
        } else {
          art.textContent = item.countLabel || item.label.slice(0, 1);
        }

        const label = document.createElement('span');
        label.textContent = item.label;
        button.append(art, label);
        fragment.append(button);
      });
      this.categories.replaceChildren(fragment);
      this.categories.hidden = false;
      this.categories.setAttribute('aria-busy', 'false');
      this.renderCategoryDots();
      this.selectCategory(this.category);
    }

    updateFavoriteCategory() {
      const button = [...this.root.querySelectorAll('[data-coloring-category]')].find(
        (candidate) => candidate.dataset.coloringCategory === FAVORITES_CATEGORY_QUERY
      );
      const icon = button?.querySelector('.cbv2-category-art .bi');
      if (icon) icon.className = `bi ${this.favoriteCodes.size ? 'bi-heart-fill' : 'bi-heart'}`;
    }

    renderCategoryDots() {
      if (!this.categoryDots) return;
      const dotCount = Math.min(5, Math.max(1, Math.ceil(this.categoryConfig.length / 2)));
      const fragment = document.createDocumentFragment();
      for (let index = 0; index < dotCount; index += 1) {
        const dot = document.createElement('i');
        if (index === 0) dot.className = 'is-active';
        fragment.append(dot);
      }
      this.categoryDots.replaceChildren(fragment);
      this.categoryDots.hidden = false;
    }

    async load({ announceSelection = true } = {}) {
      try {
        const catalog = await this.prefetchCatalog();
        this.records = catalog.products;
        this.loaded = true;
        if (this.total) this.total.textContent = `${catalog.count.toLocaleString()} designs`;
        this.renderCategories();
        this.applyFilters();

        const requestedCode = this.fragmentDesignCode || this.legacyDesignCode;
        if (requestedCode) {
          const requested = this.records.find((record) => normalize(record.designCode) === normalize(requestedCode));
          if (requested) {
            this.page.selectDesign(requested, {
              announce: announceSelection,
              updateUrl: !this.fragmentDesignCode && Boolean(this.legacyDesignCode),
            });
          } else {
            // V4 recommendations: a code the static catalog does not know may be a new
            // API-only design (SPEC §8.14). Fire-and-forget: never throws, never delays load().
            this.restoreDesignFromApi(requestedCode, {
              announce: announceSelection,
              updateUrl: !this.fragmentDesignCode && Boolean(this.legacyDesignCode),
            }).catch(() => {});
          }
        }
      } catch (error) {
        console.error('Coloring blanket V2 design catalog failed to load.', error);
        this.grid.setAttribute('aria-busy', 'false');
        this.grid.hidden = true;
        if (this.resultCount) this.resultCount.textContent = 'Catalog unavailable';
        this.page.announce('The design library could not be loaded. The default design remains selected.');
      }
    }

    applyFilters() {
      if (!this.activated || !this.loaded) return;
      const query = normalize(this.search?.value);
      const category = normalize(this.category);
      const shelf = this.shelfConfig.find((candidate) => candidate.id === this.activeShelf);
      this.filtered = this.records.filter((record) => {
        const categoryMatches =
          !category ||
          (category === FAVORITES_CATEGORY_QUERY
            ? this.isFavorite(record)
            : normalize(record.category) === category);
        if (!categoryMatches) return false;
        if (shelf && !shelf.matches(record)) return false;
        if (!query) return true;
        return recordSearchText(record).includes(query);
      });
      // Per-pill show-ahead: stable pin — the active pill's listed codes first (in list
      // order), the rest keep catalog order. Skipped during search or a shelf filter so
      // those results stay natural; Saved (favorites) is the shopper's own list.
      if (ORDER_OVERRIDES.size && !query && !shelf) {
        const activeEntry = this.categoryConfig.find(
          (item) => item.kind !== 'favorites' && normalize(item.query) === category
        );
        const orderCodes = activeEntry?.pid ? ORDER_OVERRIDES.get(activeEntry.pid) : undefined;
        if (orderCodes?.length) {
          const rank = new Map(orderCodes.map((code, index) => [code, index]));
          const pinned = [];
          const rest = [];
          this.filtered.forEach((record) => {
            (rank.has(normalize(record.designCode)) ? pinned : rest).push(record);
          });
          pinned.sort((a, b) => rank.get(normalize(a.designCode)) - rank.get(normalize(b.designCode)));
          this.filtered = pinned.concat(rest);
        }
      }
      this.currentPage = 0;
      this.render();
    }

    goToPage(pageIndex) {
      if (!this.activated || !this.loaded) return;
      const pageCount = Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
      this.currentPage = Math.min(Math.max(0, pageIndex), pageCount - 1);
      this.render();
      this.root.querySelector('.cbv2-results-heading')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    renderShelves() {
      if (!this.shelves || !this.activated || !this.loaded) return;
      const fragment = document.createDocumentFragment();
      const savedRecords = this.records.filter((record) => this.isFavorite(record));
      if (savedRecords.length) {
        fragment.append(this.createShelf({ id: 'saved', title: 'Saved designs' }, savedRecords));
      }
      this.shelfConfig.forEach((shelf) => {
        // DEV ONLY override: list = contents and order. If every code is bad, fall back
        // to the rule-based list — a shelf must never disappear because of a param typo.
        const overrideCodes = SHELF_OVERRIDES.get(normalize(shelf.id));
        const overridden = (overrideCodes || [])
          .map((code) => this.records.find((r) => normalize(r.designCode) === code))
          .filter(Boolean);
        const records = overridden.length
          ? overridden
          : this.records.filter((record) => shelf.matches(record));
        if (records.length) fragment.append(this.createShelf(shelf, records));
      });
      this.shelves.replaceChildren(fragment);
      this.shelves.hidden = !this.shelves.childElementCount;
      this.updateSelectedCards();
      this.updateFavoriteButtons();
    }

    createShelf(shelf, records) {
      const section = document.createElement('section');
      section.className = 'cbv2-product-shelf';
      section.dataset.coloringShelf = shelf.id;
      const headingId = `ColoringBlanketV2Shelf-${this.root.id}-${shelf.id}`;
      section.setAttribute('aria-labelledby', headingId);

      const heading = document.createElement('div');
      heading.className = 'cbv2-shelf-heading';
      const title = document.createElement('h3');
      title.id = headingId;
      title.textContent = shelf.title;
      const seeAll = document.createElement('button');
      seeAll.type = 'button';
      seeAll.dataset.coloringShelfSeeAll = shelf.id;
      seeAll.textContent = 'See all';
      seeAll.setAttribute('aria-label', `See all ${shelf.title.toLowerCase()}`);
      heading.append(title, seeAll);

      const rail = document.createElement('div');
      rail.className = 'cbv2-design-rail cbv2-shelf-rail';
      rail.setAttribute('aria-label', shelf.title);
      const shelfCap = shelf.id === 'saved' ? this.savedShelfSize : this.shelfSize;
      records.slice(0, shelfCap).forEach((record) => rail.append(this.createCard(record, { scope: shelf.id })));
      section.append(heading, rail);
      return section;
    }

    render() {
      if (!this.activated || !this.loaded) return;
      const pageCount = Math.max(1, Math.ceil(this.filtered.length / this.pageSize));
      const start = this.currentPage * this.pageSize;
      const pageRecords = this.filtered.slice(start, start + this.pageSize);
      const fragment = document.createDocumentFragment();
      pageRecords.forEach((record) => fragment.append(this.createCard(record, { scope: 'results' })));
      this.grid.replaceChildren(fragment);
      this.grid.setAttribute('aria-busy', 'false');

      const query = this.search?.value.trim();
      const activeShelf = this.shelfConfig.find((shelf) => shelf.id === this.activeShelf);
      const showingFavorites = this.category === FAVORITES_CATEGORY_QUERY;
      const showShelves = !query && !this.category && !this.activeShelf;
      if (this.shelves) {
        this.shelves.hidden = !showShelves;
        if (showShelves) this.renderShelves();
      }
      // V4 recommendations: own mount, own toggle; visible in every view while it has content (Ruling R37).
      this.syncRecommendationVisibility();
      this.resultTitle.textContent = query
        ? `Results for “${query}”`
        : activeShelf?.title || (showingFavorites ? 'Saved designs' : this.category || 'All designs');
      this.resultCount.textContent = `${this.filtered.length.toLocaleString()} design${
        this.filtered.length === 1 ? '' : 's'
      }`;
      if (this.emptyTitle) this.emptyTitle.textContent = showingFavorites ? 'No saved designs yet' : 'No designs found';
      if (this.emptyCopy) {
        this.emptyCopy.textContent = showingFavorites
          ? 'Use the heart on any design to save it in this browser.'
          : 'Try a broader word or clear the category filter.';
      }
      this.empty.hidden = this.filtered.length !== 0;
      this.grid.hidden = this.filtered.length === 0;
      this.pagination.hidden = this.filtered.length <= this.pageSize;
      this.previous.disabled = this.currentPage === 0;
      this.next.disabled = this.currentPage >= pageCount - 1;
      this.pageStatus.textContent = `Page ${this.currentPage + 1} of ${pageCount}`;
      this.updateSelectedCards();
      this.updateFavoriteButtons();
    }

    createCard(record, { scope = 'results' } = {}) {
      const designTitle = cleanDesignTitle(record.title) || record.title;
      const shell = document.createElement('article');
      shell.className = 'cbv2-design-card-shell';
      // V4 recommendations: cards in the recommendation grid carry a modifier class and a
      // scope marker (styling/test hook only); click, favorite and select behave the same.
      if (scope === 'recommendations') {
        shell.classList.add('cbv2-design-card-shell--recommendation');
        shell.dataset.recScope = 'recommendations';
      }
      // V4 recently viewed: history cards carry their own scope marker (styling/test hook only).
      if (scope === 'recently-viewed') {
        shell.classList.add('cbv2-design-card-shell--recently-viewed');
        shell.dataset.recScope = 'recently-viewed';
      }
      const button = document.createElement('button');
      button.className = 'cbv2-design-card';
      button.type = 'button';
      button.dataset.coloringDesignCard = '';
      button.dataset.designName = designTitle;
      button.dataset.designCode = record.designCode;
      button.dataset.designSource = record.handle;
      button.dataset.designSourceId = record.id;
      button.dataset.designImage = record.image;
      button.setAttribute('aria-pressed', 'false');
      button.setAttribute('aria-label', `Choose ${designTitle} design`);

      const imageWrap = document.createElement('span');
      imageWrap.className = 'cbv2-design-card-image';
      const image = document.createElement('img');
      image.src = sizedShopifyImage(record.image, 360);
      image.srcset = [180, 240, 360]
        .map((width) => `${sizedShopifyImage(record.image, width)} ${width}w`)
        .join(', ');
      image.sizes = '126px';
      image.alt = record.imageAlt;
      image.loading = 'lazy';
      image.decoding = 'async';
      image.width = 360;
      image.height = 360;
      const badge = document.createElement('span');
      badge.className = 'cbv2-selected-badge';
      badge.textContent = 'Selected';
      imageWrap.append(image, badge);

      const copy = document.createElement('span');
      copy.className = 'cbv2-design-card-copy';
      const title = document.createElement('strong');
      title.textContent = designTitle;
      const price = document.createElement('small');
      price.className = 'cbv2-design-card-price';
      const sourcePrice = record.sourceVariants?.find((variant) => variant.available)?.price;
      price.textContent = this.page.formatMoney(sourcePrice ?? this.page.variant?.price ?? 0);
      copy.append(title, price);
      const action = document.createElement('span');
      action.className = 'cbv2-design-card-action';
      action.textContent = 'Choose this design';
      button.append(imageWrap, copy, action);

      button.addEventListener('click', () => {
        this.page.selectDesign(record);
        this.updateSelectedCards();
        this.page.openModal(button);
      });

      const favorite = document.createElement('button');
      favorite.className = 'cbv2-favorite-button';
      favorite.type = 'button';
      favorite.dataset.coloringFavorite = '';
      favorite.dataset.designCode = record.designCode;
      favorite.dataset.favoriteScope = scope;
      favorite.setAttribute('aria-pressed', String(this.isFavorite(record)));
      favorite.setAttribute(
        'aria-label',
        `${this.isFavorite(record) ? 'Remove' : 'Add'} ${designTitle} ${this.isFavorite(record) ? 'from' : 'to'} favorites`
      );
      const favoriteIcon = document.createElement('i');
      favoriteIcon.className = `bi ${this.isFavorite(record) ? 'bi-heart-fill' : 'bi-heart'}`;
      favoriteIcon.setAttribute('aria-hidden', 'true');
      favorite.append(favoriteIcon);
      favorite.addEventListener('click', () => this.toggleFavorite(record, favorite));

      shell.append(button, favorite);
      return shell;
    }

    updateSelectedCards() {
      this.root.querySelectorAll('[data-coloring-design-card]').forEach((card) => {
        const selected = normalize(card.dataset.designCode) === normalize(this.page.design.designCode);
        card.classList.toggle('is-selected', selected);
        card.setAttribute('aria-pressed', String(selected));
      });
    }

    updateFavoriteButtons() {
      this.root.querySelectorAll('[data-coloring-favorite]').forEach((button) => {
        const active = this.favoriteCodes.has(normalize(button.dataset.designCode));
        const card = button.closest('.cbv2-design-card-shell');
        const designTitle = card?.querySelector('[data-coloring-design-card]')?.dataset.designName || 'design';
        button.classList.toggle('is-favorite', active);
        button.setAttribute('aria-pressed', String(active));
        button.setAttribute('aria-label', `${active ? 'Remove' : 'Add'} ${designTitle} ${active ? 'from' : 'to'} favorites`);
        const icon = button.querySelector('.bi');
        if (icon) icon.className = `bi ${active ? 'bi-heart-fill' : 'bi-heart'}`;
      });
    }

    // ===================================================================================
    // V4 RECOMMENDATIONS — START (2026-08-27). Design-level recommendations for the
    // selected design: SPEC §8 (items 1-14 + new-design requirement), SPEC §11 (fallback
    // table) and implementation/CONTRACT.md release 1 (GET /recommendations,
    // GET /designs/{page_id}, §4 record rules). Everything the feature needs sits between
    // the START/END banners plus five one-line hooks marked "V4 recommendations:"
    // (constructor, prefetchCatalog, load, render, createCard). Release 1 sends no
    // analytics, reads no privacy API and never calls an app proxy.
    //
    // Failure policy: every entry point below catches its own errors, and a failure only
    // ever hides [data-coloring-recommendations]; the V3 browse/select/cart flow never
    // waits on or depends on this block.
    // ===================================================================================

    // Config is read once from the data attributes on [data-coloring-results] (section
    // settings). apiBase is null when the feature is disabled or the setting is empty:
    // fallback-only mode, no API call is ever made (CONTRACT §1).
    initRecommendations() {
      const data = this.root.dataset;
      const mount = this.root.querySelector('[data-coloring-recommendations]');
      const grid = mount ? mount.querySelector('[data-coloring-recommendations-grid]') : null;
      const enabled = data.recommendationEnabled !== 'false';
      const apiBase = String(data.recommendationApiBase || '').trim().replace(/\/+$/, '');
      this.recs = {
        mount: mount && grid ? mount : null,
        grid: mount && grid ? grid : null,
        enabled,
        apiBase: enabled && apiBase ? apiBase : null,
        productType: String(data.recommendationProductType || '').trim() || 'coloring_blanket',
        timeoutMs: Math.max(1, Number(data.recommendationTimeoutMs) || 4000),
        limit: Math.min(12, Math.max(1, Math.floor(Number(data.recommendationLimit) || 12))),
        token: 0, // monotonic request token: only the newest request may touch the DOM
        controller: null, // AbortController of the in-flight request
        started: false,
        nearViewport: false,
        catalogSettled: false,
        hasContent: false,
        currentCode: '',
      };
      try {
        if (!this.recs.mount) return;
        if (!enabled) {
          this.recs.mount.hidden = true;
          return;
        }
        // SPEC §8.5: refresh on the design-change event using the event's design code.
        document.addEventListener('coloring:design-change', (event) => {
          try {
            this.onRecommendationDesignChange(event);
          } catch (error) {
            this.failRecommendations(error);
          }
        });
        this.watchRecommendationViewport();
      } catch (error) {
        this.failRecommendations(error);
      }
    }

    // SPEC §8.4 lazy start: the first request waits until the recommendation area is
    // within 200 px of the viewport AND the static catalog fetch has settled (success or
    // failure), so the local fallback can answer at once when the API cannot. The mount
    // itself is display:none until it has content (no box to observe), so the observer
    // watches the results section that contains it; without IntersectionObserver the
    // area counts as visible right away.
    watchRecommendationViewport() {
      const markNear = () => {
        this.recs.nearViewport = true;
        this.maybeStartRecommendations();
      };
      if (typeof window.IntersectionObserver !== 'function') {
        markNear();
        return;
      }
      const observer = new window.IntersectionObserver(
        (entries) => {
          try {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            markNear();
          } catch (error) {
            this.failRecommendations(error);
          }
        },
        { rootMargin: '200px 0px' }
      );
      observer.observe(this.root);
    }

    // Called from the prefetchCatalog hook whenever the catalog promise settles.
    markCatalogSettled() {
      try {
        if (!this.recs || this.recs.catalogSettled) return;
        this.recs.catalogSettled = true;
        // One microtask later, so a #design= restoration that load() applies right after
        // the catalog resolves becomes the source of the first request instead of the
        // default design (saves one aborted request on deep links).
        Promise.resolve().then(() => this.maybeStartRecommendations());
      } catch (error) {
        this.failRecommendations(error);
      }
    }

    maybeStartRecommendations() {
      try {
        const recs = this.recs;
        if (!recs || !recs.mount || recs.started || !recs.nearViewport || !recs.catalogSettled) return;
        recs.started = true;
        this.refreshRecommendations(this.currentRecommendationSource());
      } catch (error) {
        this.failRecommendations(error);
      }
    }

    // The source is the active designCode: the page controller's selected design, or the
    // main section's default design code while a variant-backed "Design N" is active
    // (those carry no catalog code).
    currentRecommendationSource() {
      const active = this.page && this.page.design ? this.page.design.designCode : '';
      const fallback = this.page && this.page.root ? this.page.root.dataset.defaultDesignCode : '';
      return String(active || fallback || '').trim();
    }

    onRecommendationDesignChange(event) {
      const code = String(event?.detail?.design?.designCode || '').trim();
      if (!code || !this.recs || !this.recs.started) return;
      this.refreshRecommendations(code);
    }

    // One request per selection. The previous in-flight request is aborted, the timeout
    // aborts through the same controller, and a response is applied only while its token
    // is current and its source_page_id is the requested design (SPEC §8.6).
    refreshRecommendations(code) {
      const recs = this.recs;
      if (!recs || !recs.mount) return;
      try {
        const sourceCode = String(code || '').trim();
        recs.currentCode = sourceCode;
        recs.token += 1;
        const token = recs.token;
        if (recs.controller) recs.controller.abort();
        recs.controller = null;
        if (!recs.apiBase || !sourceCode) {
          this.applyRecommendationFallback(sourceCode, recs.apiBase ? 'no-source' : 'api-disabled');
          return;
        }
        const controller = new AbortController();
        recs.controller = controller;
        const timer = window.setTimeout(() => controller.abort(), recs.timeoutMs);
        const url =
          `${recs.apiBase}/recommendations?page_id=${encodeURIComponent(sourceCode)}` +
          `&product_type=${encodeURIComponent(recs.productType)}&limit=${recs.limit}`;
        this.fetchRecommendationJson(url, controller.signal)
          .then((payload) => {
            if (token !== recs.token) return;
            const records = this.validateRecommendationPayload(payload, sourceCode);
            if (!records.length) {
              const envelopeOk = payload && typeof payload === 'object' && payload.schema_version === 1 && Array.isArray(payload.recommendations);
              throw this.recommendationError(envelopeOk ? 'no-valid-items' : 'invalid-payload');
            }
            this.renderRecommendations(records);
          })
          .catch((error) => {
            // Network error, abort/timeout, non-2xx (404 unknown_source, 503 not_ready,
            // ...), invalid JSON, invalid payload or zero valid items: SPEC §11 -> local
            // fallback. A stale token means a newer request owns the mount now.
            if (token !== recs.token) return;
            const reason = error && error.recsReason ? error.recsReason : error && error.name === 'AbortError' ? 'timeout' : 'network';
            this.applyRecommendationFallback(sourceCode, reason);
          })
          .finally(() => {
            window.clearTimeout(timer);
            if (recs.controller === controller) recs.controller = null;
          });
      } catch (error) {
        this.failRecommendations(error);
      }
    }

    async fetchRecommendationJson(url, signal) {
      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        mode: 'cors',
        credentials: 'omit',
        signal,
      });
      if (!response.ok) throw this.recommendationError(`http-${response.status}`);
      try {
        return await response.json();
      } catch (_error) {
        throw this.recommendationError('invalid-json');
      }
    }

    // Expected failures carry a short reason so the fallback can be traced with
    // console.debug during a preview; the reason never reaches the DOM.
    recommendationError(reason) {
      const error = new Error(`Recommendations unavailable: ${reason}.`);
      error.recsReason = reason;
      return error;
    }

    debugRecommendations(message) {
      try {
        if (typeof console !== 'undefined' && typeof console.debug === 'function') {
          console.debug('[coloring-blanket-v4] recommendations', message);
        }
      } catch (_error) {
        /* logging must never matter */
      }
    }

    // CONTRACT §2 envelope + §4 records. Invalid items are dropped one by one; the result
    // is deduplicated by normalized page_id, never contains the source, is sorted by
    // display_rank and capped at the limit. An empty result counts as a failure.
    validateRecommendationPayload(payload, sourceCode) {
      if (!payload || typeof payload !== 'object' || payload.schema_version !== 1) return [];
      if (normalize(payload.source_page_id) !== normalize(sourceCode)) return [];
      if (!Array.isArray(payload.recommendations)) return [];
      const sourceKey = normalize(sourceCode);
      const seen = new Set();
      const items = [];
      payload.recommendations.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        if (typeof item.page_id !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(item.page_id)) return;
        if (!Number.isInteger(item.display_rank) || item.display_rank < 1) return;
        const record = this.validateRecommendationRecord(item.record);
        if (!record) return;
        const key = normalize(item.page_id);
        const codeKey = normalize(record.designCode);
        // Unique by page_id AND by designCode: two items sharing a designCode would render
        // twice and both light up as selected (CONTRACT: designCode == page_id unless proven).
        if (key === sourceKey || codeKey === sourceKey || seen.has(key) || seen.has(codeKey)) return;
        seen.add(key);
        seen.add(codeKey);
        items.push({ rank: item.display_rank, record });
      });
      items.sort((a, b) => a.rank - b.rank);
      return items.slice(0, this.recs.limit).map((item) => item.record);
    }

    // CONTRACT §4: exact keys and rules; any failure drops the whole record (review S8),
    // and only the contract keys are copied so nothing unexpected reaches cart state.
    validateRecommendationRecord(record) {
      if (!record || typeof record !== 'object' || Array.isArray(record)) return null;
      const text = (value, max) => typeof value === 'string' && value.length <= max;
      const digits = (value) => typeof value === 'string' && /^\d{1,20}$/.test(value);
      const cdnImage = (value) =>
        typeof value === 'string' && value.length <= 2048 && value.startsWith('https://cdn.shopify.com/');
      const cents = (value) => Number.isInteger(value) && value >= 0;
      if (!digits(record.id)) return null;
      if (!text(record.handle, 255) || !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(record.handle)) return null;
      if (!text(record.title, 300) || !record.title.trim()) return null;
      if (typeof record.designCode !== 'string' || !/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/.test(record.designCode)) return null;
      if (!cdnImage(record.image)) return null;
      if (!text(record.imageAlt, 300)) return null;
      if (record.coverImage !== null && !cdnImage(record.coverImage)) return null;
      if (!text(record.coverImageAlt, 300)) return null;
      if (!text(record.category, 64)) return null;
      if (!Array.isArray(record.sourceVariants) || !record.sourceVariants.length) return null;
      const sourceVariants = [];
      for (const variant of record.sourceVariants) {
        if (!variant || typeof variant !== 'object') return null;
        if (!digits(variant.sourceVariantId) || !text(variant.size, 64) || !cents(variant.price)) return null;
        if (variant.compareAtPrice !== null && !cents(variant.compareAtPrice)) return null;
        if (typeof variant.available !== 'boolean') return null;
        sourceVariants.push({
          sourceVariantId: variant.sourceVariantId,
          size: variant.size,
          price: variant.price,
          compareAtPrice: variant.compareAtPrice,
          available: variant.available,
        });
      }
      if (!sourceVariants.some((variant) => variant.available)) return null;
      return {
        id: record.id,
        handle: record.handle,
        title: record.title,
        designCode: record.designCode,
        image: record.image,
        imageAlt: record.imageAlt,
        coverImage: record.coverImage,
        coverImageAlt: record.coverImageAlt,
        category: record.category,
        sourceVariants,
      };
    }

    // SPEC §11 local fallback from the static catalog (already fetched for the browser):
    // same category as the source when the source is a catalog record, otherwise stable
    // catalog order; the source is excluded, design codes are unique, at most `limit`
    // records. Returns null when the catalog is unavailable (the caller hides the mount).
    buildStaticFallback(code) {
      const products = this.catalog && Array.isArray(this.catalog.products) ? this.catalog.products : null;
      if (!products) return null;
      const sourceKey = normalize(code);
      const source = sourceKey ? products.find((record) => normalize(record?.designCode) === sourceKey) : null;
      const category = normalize(source ? source.category : '');
      // One record per designCode in first-seen catalog order; when a code maps to several
      // products (the Sherpa/fleece pair) the Sherpa product wins (SPEC section 1.2: array
      // order must not silently decide). Map keeps the first occurrence's position.
      const isSherpa = (record) => /sherpa/.test(String(record?.handle || '').toLowerCase());
      const pick = (pool) => {
        const preferred = new Map();
        for (const record of pool) {
          const key = normalize(record?.designCode);
          if (!key || key === sourceKey) continue;
          const current = preferred.get(key);
          if (!current || (!isSherpa(current) && isSherpa(record))) preferred.set(key, record);
        }
        return [...preferred.values()].slice(0, this.recs.limit);
      };
      const sameCategory = category ? pick(products.filter((record) => normalize(record?.category) === category)) : [];
      return sameCategory.length ? sameCategory : pick(products);
    }

    applyRecommendationFallback(code, reason) {
      try {
        this.debugRecommendations(`fallback: ${reason || 'unknown'}`);
        const records = this.buildStaticFallback(code);
        if (records && records.length) this.renderRecommendations(records);
        else this.hideRecommendations();
      } catch (error) {
        this.failRecommendations(error);
      }
    }

    // SPEC §8.7: the shelves' own card factory and click path (selectDesign -> modal ->
    // legacy variant/cart mapping), scoped 'recommendations'. Records absent from the
    // static catalog work because selectDesign reads everything from the record itself.
    renderRecommendations(records) {
      const recs = this.recs;
      recs.grid.replaceChildren(...records.map((record) => this.createCard(record, { scope: 'recommendations' })));
      recs.hasContent = recs.grid.childElementCount > 0;
      this.updateSelectedCards();
      this.updateFavoriteButtons();
      this.syncRecommendationVisibility();
    }

    hideRecommendations() {
      const recs = this.recs;
      if (!recs || !recs.mount) return;
      recs.hasContent = false;
      recs.grid.replaceChildren();
      recs.mount.hidden = true;
    }

    // Ruling R37 (2026-08-28, owner decision, replaces R8/R17 for visibility): the mount is
    // shown in every view as long as it has content, including search, category and shelf
    // views, because the shopper keeps picking designs there and the rail follows the
    // selected design (SPEC §8.5). The mount sits before the shelves and the browse grid,
    // so in a filtered view it renders right under the category pills. It is toggled on its
    // own, so renderShelves() never touches it.
    syncRecommendationVisibility() {
      try {
        const recs = this.recs;
        if (!recs || !recs.mount) return;
        recs.mount.hidden = !recs.hasContent;
      } catch (error) {
        this.failRecommendations(error);
      }
    }

    // SPEC §8.14: a #design= code the static catalog does not know is asked from the API
    // (same timeout and record rules); any failure keeps the V3 outcome (default design).
    async restoreDesignFromApi(code, options) {
      let timer = 0;
      try {
        const recs = this.recs;
        const requestedCode = String(code || '').trim();
        if (!recs || !recs.apiBase || !requestedCode) return;
        // The shopper may move on while the request is in flight (a card tap, a Design
        // swatch): selectDesign/applyVariantDesign assign a fresh design object, so identity
        // detects any change and a late answer is dropped (same rule as loadLegacyRailMedia).
        const before = this.page ? this.page.design : null;
        const controller = new AbortController();
        timer = window.setTimeout(() => controller.abort(), recs.timeoutMs);
        const url =
          `${recs.apiBase}/designs/${encodeURIComponent(requestedCode)}` +
          `?product_type=${encodeURIComponent(recs.productType)}`;
        const payload = await this.fetchRecommendationJson(url, controller.signal);
        const record =
          payload && typeof payload === 'object' && payload.schema_version === 1 && normalize(payload.page_id) === normalize(requestedCode)
            ? this.validateRecommendationRecord(payload.record)
            : null;
        if (!record || !this.page || this.page.design !== before) return;
        this.page.selectDesign(record, options);
      } catch (_error) {
        /* keep the default design (V3 behavior) */
      } finally {
        if (timer) window.clearTimeout(timer);
      }
    }

    // Last resort for unexpected exceptions: log, hide the mount, leave the page alone.
    failRecommendations(error) {
      console.error('Coloring blanket V4 recommendations failed; the recommendation area is hidden.', error);
      try {
        this.hideRecommendations();
      } catch (_error) {
        /* nothing else to do */
      }
    }
    // ===================================================================================
    // V4 RECOMMENDATIONS — END
    // ===================================================================================

    // ===================================================================================
    // V4 RECENTLY VIEWED — START (2026-08-31). Browser-local history of the designs the
    // shopper actually selected, so returning to a design does not depend on the Saved
    // heart (implementation/RECENTLY_VIEWED_BRIEF.md in the discovery project). Records
    // only on coloring:design-change (the selectDesign choke point); stores the display
    // record itself so a design that exists only in the API keeps rendering after a
    // reload; newest first, deduplicated by design code, capped at 20; the rail stays
    // hidden until 3 entries besides the current design exist. localStorage only - no
    // network, no analytics, no privacy API. Failure policy mirrors favorites and
    // recommendations: a storage or render error only ever hides
    // [data-coloring-recently-viewed]; the browse/select/cart flow never waits on it.
    // ===================================================================================

    initRecentlyViewed() {
      const mount = this.root.querySelector('[data-coloring-recently-viewed]');
      const grid = mount ? mount.querySelector('[data-coloring-recently-viewed-grid]') : null;
      const enabled = this.root.dataset.recentlyViewedEnabled !== 'false';
      this.recentlyViewed = {
        mount: mount && grid ? mount : null,
        grid: mount && grid ? grid : null,
        enabled,
        limit: 20,
        minVisible: 3,
      };
      try {
        if (!this.recentlyViewed.mount) return;
        if (!enabled) {
          this.recentlyViewed.mount.hidden = true;
          return;
        }
        document.addEventListener('coloring:design-change', (event) => {
          try {
            this.onRecentlyViewedDesignChange(event);
          } catch (error) {
            this.failRecentlyViewed(error);
          }
        });
        this.renderRecentlyViewed();
      } catch (error) {
        this.failRecentlyViewed(error);
      }
    }

    // A stored entry must carry the record fields createCard/selectDesign read; anything
    // else is dropped on read so corrupted storage never reaches the DOM or cart state.
    validRecentlyViewedEntry(entry) {
      if (!entry || typeof entry !== 'object' || Array.isArray(entry)) return false;
      const record = entry.record;
      if (typeof entry.code !== 'string' || !entry.code.trim()) return false;
      if (!record || typeof record !== 'object' || Array.isArray(record)) return false;
      const text = (value) => typeof value === 'string';
      if (!text(record.title) || !record.title.trim()) return false;
      if (!text(record.designCode) || !record.designCode.trim()) return false;
      if (!text(record.handle) || !record.handle.trim()) return false;
      if (!text(record.image) || !record.image.trim()) return false;
      if (!Array.isArray(record.sourceVariants)) return false;
      return true;
    }

    readRecentlyViewed() {
      try {
        const stored = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_STORAGE_KEY) || '[]');
        if (!Array.isArray(stored)) return [];
        return stored.filter((entry) => this.validRecentlyViewedEntry(entry)).slice(0, this.recentlyViewed.limit);
      } catch (_error) {
        return [];
      }
    }

    persistRecentlyViewed(entries) {
      try {
        window.localStorage.setItem(RECENTLY_VIEWED_STORAGE_KEY, JSON.stringify(entries));
      } catch (_error) {
        /* history is a convenience; blocked or full storage must never break the page */
      }
    }

    onRecentlyViewedDesignChange(event) {
      const rv = this.recentlyViewed;
      if (!rv || !rv.mount || !rv.enabled) return;
      const design = event?.detail?.design;
      if (!design?.title || !design?.designCode || !design?.handle || !design?.image) return;
      const entry = {
        code: String(design.designCode),
        at: Date.now(),
        record: {
          id: String(design.id || ''),
          handle: design.handle,
          title: design.title,
          designCode: design.designCode,
          image: design.image,
          imageAlt: design.imageAlt || '',
          coverImage: design.coverImage || design.image,
          coverImageAlt: design.coverImageAlt || '',
          category: String(design.category || ''),
          sourceVariants: Array.isArray(design.sourceVariants) ? design.sourceVariants : [],
        },
      };
      const key = normalize(entry.code);
      const entries = [entry, ...this.readRecentlyViewed().filter((existing) => normalize(existing.code) !== key)].slice(0, rv.limit);
      this.persistRecentlyViewed(entries);
      this.renderRecentlyViewed();
    }

    // The current design is excluded (it is already on the page); with fewer than
    // minVisible remaining entries the whole rail stays hidden so first-time visitors
    // never see a nearly empty heading.
    renderRecentlyViewed() {
      const rv = this.recentlyViewed;
      if (!rv || !rv.mount || !rv.enabled) return;
      const currentKey = normalize(this.page && this.page.design ? this.page.design.designCode : '');
      const records = this.readRecentlyViewed()
        .filter((entry) => normalize(entry.code) !== currentKey)
        .map((entry) => entry.record);
      if (records.length < rv.minVisible) {
        rv.grid.replaceChildren();
        rv.mount.hidden = true;
        return;
      }
      rv.grid.replaceChildren(...records.map((record) => this.createCard(record, { scope: 'recently-viewed' })));
      this.updateSelectedCards();
      this.updateFavoriteButtons();
      rv.mount.hidden = false;
    }

    // Last resort for unexpected exceptions: log, hide the rail, leave the page alone.
    failRecentlyViewed(error) {
      console.error('Coloring blanket V4 recently viewed failed; the history area is hidden.', error);
      try {
        const rv = this.recentlyViewed;
        if (rv && rv.mount) {
          rv.grid.replaceChildren();
          rv.mount.hidden = true;
        }
      } catch (_error) {
        /* nothing else to do */
      }
    }
    // ===================================================================================
    // V4 RECENTLY VIEWED — END
    // ===================================================================================
  }

  const initialize = () => {
    const pageRoot = document.querySelector('[data-coloring-page]');
    if (!pageRoot || pageRoot.dataset.coloringV2Initialized === 'true') return;
    pageRoot.dataset.coloringV2Initialized = 'true';
    const page = new ColoringBlanketV2Page(pageRoot);
    document.querySelectorAll('[data-coloring-results]').forEach((root) => {
      new ColoringBlanketV2Results(root, page);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
